import { useEffect, useRef, useState } from "react";
import { useTranslator } from "@eo-locale/react";

import { Box, IconButton, MdSort } from "@/storybook-components";
import { highlightText } from "@/helpers/highlight-text";
import { scrollIntoView } from "@/helpers/scroll-into-view";
import {
  upDownArrowAccessibility,
  clearAccessibilityFn
} from "@/helpers/up-down-arrow-accessibility";
import PopoverAction from "../common/popover";
import useClickOutsideHandler from "@/hooks/useClickOutsideHandler";
import { useAppSelector } from "@/redux/store/hooks";

const SortContent = ({
  isOpen,
  highlightClass = "",
  locale,
  action,
  resetPage,
  ...sortParams
}): JSX.Element => {
  const { translate } = useTranslator();
  const { allowCommentsForIdeas, disableVoting } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );
  const sortTypeList = [
    "newest",
    "oldest",
    ...(disableVoting ? [] : ["mostVotes"])
  ];

  if (allowCommentsForIdeas) {
    sortTypeList.push("mostComments");
  }

  const [sortValue, setSortValue] = useState("newest");

  const sortRef = useRef(null);

  const { handleActive } = useClickOutsideHandler(sortRef);

  // for accessibility
  useEffect(() => {
    if (!handleActive) clearAccessibilityFn();
  }, [handleActive]);

  const triggerAccessibility = () => {
    upDownArrowAccessibility("sort-item", "sort-button");
  };

  const handleClick = async (sortType) => {
    if (sortType === sortValue) {
      scrollIntoView("ideas-grid");
      return;
    }
    if (sortParams.search)
      await highlightText(highlightClass, sortParams.search);
    scrollIntoView("ideas-grid");
    setSortValue(sortType);
    resetPage(1);
    switch (sortType) {
      case "oldest":
        return fetchSortedList("created_at", "asc");
      case "mostVotes":
        return fetchSortedList("idea_votes_count", "desc");
      case "mostComments":
        return fetchSortedList("comments_count", "desc");
      default:
        return fetchSortedList("created_at", "desc");
    }
  };
  const fetchSortedList = async (sort, order) => {
    action(sort, order, sortParams.search);
    if (sortParams.search) highlightText(highlightClass, sortParams.search);
  };

  return (
    <PopoverAction
      content={
        <Box
          py={2}
          sx={{
            "& > div": {
              color: "gray.800",
              padding: "12px",
              "&:hover": {
                backgroundColor: "gray.100",
                cursor: "pointer"
              }
            }
          }}
        >
          {sortTypeList.map((value, index) => {
            return (
              <Box
                bg={sortValue === value ? "gray.100" : "white"}
                key={index}
                tabIndex={0}
                onClick={() => handleClick(value)}
                className="sort-item"
              >
                {translate(`${locale}.${value}`)}
              </Box>
            );
          })}
        </Box>
      }
      triggerIcon={
        <IconButton
          aria-label={translate(`${locale}.sortBy`)}
          icon={<MdSort />}
          variant="solid"
          w={{ base: 10, sm: 12, xl: 10 }}
          h={{ base: 10, sm: 12, xl: 10 }}
          padding={2.5}
          justifyContent="center"
          alignItems="center"
          borderRadius="50%"
          bg={handleActive ? "gray.300" : "white"}
          color="gray.900"
          boxShadow="lg"
          _hover={
            handleActive
              ? { bg: "gray.300", color: "gray.900" }
              : { bg: "white", color: "gray.900" }
          }
          _focus={{
            bg: "white",
            color: "gray.900"
          }}
          ref={sortRef}
          onClick={triggerAccessibility}
          className="sort-button"
        />
      }
      borderColor="gray.200"
      maxW={56}
    />
  );
};

export default SortContent;
